/* eslint-env browser */
// Customer / driver details › Trips (wave 3, W2_Portal_Tables look): this
// person's trips in the modern table. Fares show as on Booking history:
// the amount with "Fixed price" when the customer set it, "≈ Estimate" while
// nobody changed it, or the final fare with the system estimate under it
// (fareInfoOf / FareTag come from BookingHistory, owner rule
// fixed-price-vs-estimate). Clicking a trip opens its details.
//
// Data, exactly as before: the bookings already loaded (bookinglistdata,
// the last 30 days) filtered to this person; "Show all trips" makes the one
// indexed read of this person's bookings (bookingListRef(uid, role)) only
// when pressed. Exports for admins (CSV only with critical edits on); the
// other person's phone is hidden in demo mode. "← Back" is the page's own.
// Call-centre feedback (29 Oct 2026): a copy button beside the other
// person's number copies it without spaces (+251964540448) for MicroSIP; it
// never opens the trip and is not there while numbers are hidden.
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { get } from 'firebase/database';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { FirebaseContext } from 'common';
import { tokens as c } from '../theme';
import ClickableBookingLink from '../components/ClickableBookingLink';
import { CopyButton, DataTable, calmCaps, formatMoney } from '../components/modern';
import { BookingStatusChip, bookingStatusLabel, fareInfoOf, FareTag, formatAmount } from './BookingHistory';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

export default function UserRides({ data, tabid }) {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const [bookingData, setBookingData] = useState([]);
  const navigate = useNavigate();
  const { state } = useLocation();
  const firebase = useContext(FirebaseContext);
  const [showingAll, setShowingAll] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  // Once "Show all trips" has loaded the full list, a later change to the
  // loaded bookings or to the person no longer swaps it back to 30 days.
  const showingAllRef = useRef(false);

  useEffect(() => {
    if (showingAllRef.current) return;
    if (bookinglistdata.bookings) {
      if (data.usertype === 'customer') {
        setBookingData(bookinglistdata.bookings.filter((item) => item.customer === id));
      } else if (data.usertype === 'driver') {
        setBookingData(bookinglistdata.bookings.filter((item) => item.driver === id));
      }
    } else {
      setBookingData([]);
    }
  }, [bookinglistdata.bookings, id, data]);

  const handleShowAllTrips = () => {
    if (!firebase || !data || !id) return;
    setLoadingAll(true);
    const role = data.usertype === 'customer' ? 'customer' : 'driver';
    const dbRef = firebase.bookingListRef(id, role);
    get(dbRef)
      .then((snapshot) => {
        if (snapshot.val()) {
          const raw = snapshot.val();
          const bookings = Object.keys(raw).map((key) => {
            const item = raw[key];
            item.id = key;
            item.pickupAddress = item.pickup ? item.pickup.add : '';
            item.dropAddress = item.drop ? item.drop.add : '';
            item.discount = item.discount || 0;
            item.cashPaymentAmount = item.cashPaymentAmount || 0;
            item.cardPaymentAmount = item.cardPaymentAmount || 0;
            item.bookingDate = item.tripdate || item.bookingDate;
            return item;
          });
          showingAllRef.current = true;
          setBookingData(bookings.reverse());
          setShowingAll(true);
        }
        setLoadingAll(false);
      })
      .catch((error) => {
        console.error('Error fetching all user bookings:', error);
        setLoadingAll(false);
      });
  };

  const isDriverPage = data?.usertype === 'driver';
  const isCustomerPage = data?.usertype === 'customer';
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const isAdmin = auth.profile && auth.profile.usertype === 'admin';
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const decimals = settings && settings.decimal;
  const withSymbol = (amount) => formatMoney(amount, settings, true);

  const fareKindLabel = (row) => {
    const kind = fareInfoOf(row).kind;
    if (kind === 'fixed') return t('fixed_price', { defaultValue: 'Fixed price' });
    if (kind === 'estimate') return t('estimated_fare', { defaultValue: 'Estimate' });
    return t('bookings_fare_final', { defaultValue: 'Final' });
  };

  const renderFare = (row) => {
    const info = fareInfoOf(row);
    if (!info.amount) return <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>;
    const amount = formatAmount(info.amount, decimals);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
        <Box sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{info.kind === 'estimate' ? `~${amount}` : amount}</Box>
        {info.kind === 'final' ? (
          info.estimate ? (
            <Box sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {t('bookings_est_short', { amount: formatAmount(info.estimate, decimals), defaultValue: 'est. {{amount}}' })}
            </Box>
          ) : null
        ) : (
          <FareTag kind={info.kind} />
        )}
      </Box>
    );
  };

  const nameCol = (field, title) => ({ id: field, title, field, render: (row) => row[field] || '-' });
  const contactCol = (field, title) => ({
    id: field,
    title,
    field,
    searchValue: (row) => (allowPrivate ? row[field] || '' : ''),
    sortValue: (row) => (allowPrivate ? row[field] || '' : ''),
    render: (row) => (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {allowPrivate ? row[field] || '-' : hidden}
        {allowPrivate && row[field] ? <CopyButton value={row[field]} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
      </Box>
    ),
  });

  const columns = [
    {
      id: 'status',
      title: calmCaps(t('booking_status')),
      field: 'status',
      searchValue: (row) => bookingStatusLabel(row.status, t),
      sortValue: (row) => bookingStatusLabel(row.status, t),
      render: (row) => (row.status ? <BookingStatusChip status={row.status} /> : '—'),
    },
    {
      id: 'reference',
      title: t('booking_ref'),
      field: 'reference',
      render: (row) =>
        row.reference ? (
          <ClickableBookingLink value={row.reference} searchBy="reference" bookingId={row.id} iconType="receipt" state={{ pageNo: state?.pageNo }} />
        ) : (
          '-'
        ),
    },
    ...(isDriverPage
      ? [
          nameCol('customer_name', t('customer_name', { defaultValue: 'Customer Name' })),
          contactCol('customer_contact', t('customer_contact', { defaultValue: 'Customer Phone' })),
        ]
      : []),
    ...(isCustomerPage
      ? [nameCol('driver_name', t('driver_name', { defaultValue: 'Driver Name' })), contactCol('driver_contact', t('driver_contact', { defaultValue: 'Driver Phone' }))]
      : []),
    { id: 'bookingDate', title: t('booking_date'), field: 'bookingDate', type: 'datetime', nowrap: true },
    { id: 'pickupAddress', title: t('pickup_address'), field: 'pickupAddress', minWidth: 180 },
    { id: 'dropAddress', title: t('drop_address'), field: 'dropAddress', minWidth: 180 },
    {
      id: 'fare',
      title: t('fare', { defaultValue: 'Fare' }),
      align: 'right',
      sortValue: (row) => fareInfoOf(row).amount || null,
      searchValue: (row) => `${fareInfoOf(row).amount || ''} ${fareKindLabel(row)}`,
      render: renderFare,
    },
    {
      id: 'estimate',
      title: t('system_estimate', { defaultValue: 'System estimate' }),
      field: 'estimate',
      type: 'money',
      defaultHidden: true,
      render: (row) => (row.estimate ? withSymbol(row.estimate) : '-'),
    },
    {
      id: 'trip_cost',
      title: t('trip_cost', { defaultValue: 'Trip Cost' }),
      field: 'trip_cost',
      type: 'money',
      defaultHidden: true,
      render: (row) => (row.trip_cost ? withSymbol(parseFloat(row.trip_cost).toFixed(2)) : '-'),
    },
    {
      id: 'payment_mode',
      title: calmCaps(t('payment_mode', { defaultValue: 'Payment Mode' })),
      field: 'payment_mode',
      searchValue: (row) => (row.payment_mode ? t(row.payment_mode) : ''),
      render: (row) => (row.payment_mode ? t(row.payment_mode) : '-'),
    },
  ];

  // The CSV keeps the old columns (and adds the fare type, as Booking
  // history does); the other person's phone stays hidden in demo mode.
  const exportColumns = [
    { title: calmCaps(t('booking_status')), value: (row) => bookingStatusLabel(row.status, t) },
    { title: t('booking_ref'), field: 'reference' },
    ...(isDriverPage
      ? [
          { title: t('customer_name', { defaultValue: 'Customer Name' }), field: 'customer_name' },
          { title: t('customer_contact', { defaultValue: 'Customer Phone' }), value: (row) => (allowPrivate ? row.customer_contact || '' : hidden) },
        ]
      : []),
    ...(isCustomerPage
      ? [
          { title: t('driver_name', { defaultValue: 'Driver Name' }), field: 'driver_name' },
          { title: t('driver_contact', { defaultValue: 'Driver Phone' }), value: (row) => (allowPrivate ? row.driver_contact || '' : hidden) },
        ]
      : []),
    { title: t('booking_date'), field: 'bookingDate', type: 'datetime' },
    { title: t('pickup_address'), field: 'pickupAddress' },
    { title: t('drop_address'), field: 'dropAddress' },
    { title: t('system_estimate', { defaultValue: 'System estimate' }), field: 'estimate', align: 'right' },
    { title: t('trip_cost', { defaultValue: 'Trip Cost' }), field: 'trip_cost', align: 'right' },
    { title: t('bookings_col_fare_kind', { defaultValue: 'Fare type' }), value: fareKindLabel },
    { title: calmCaps(t('payment_mode', { defaultValue: 'Payment Mode' })), value: (row) => (row.payment_mode ? t(row.payment_mode) : '') },
  ];

  const title = calmCaps(t('bookings_table_title', { defaultValue: 'Trips' }));
  const listLoading = !bookinglistdata.bookings && !!bookinglistdata.loading && !showingAll;
  const showAllLabel = t('user_trips_show_all', { defaultValue: 'Show all trips' });

  const countText = ({ shown, total, query, loading }) => {
    if (loading) return t('loading', { defaultValue: 'Loading…' });
    if (query) return t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' });
    return showingAll
      ? t('user_trips_all', { count: total, n: fmt(total), defaultValue_one: '{{n}} trip in total', defaultValue_other: '{{n}} trips in total', defaultValue: '{{n}} trips in total' })
      : t('user_trips_recent', {
          count: total,
          n: fmt(total),
          defaultValue_one: '{{n}} trip in the last 30 days',
          defaultValue_other: '{{n}} trips in the last 30 days',
          defaultValue: '{{n}} trips in the last 30 days',
        });
  };

  return (
    <Box dir={i18n.dir()}>
      <DataTable
        title={title}
        rows={bookingData}
        columns={columns}
        loading={listLoading}
        refreshing={loadingAll}
        countText={countText}
        toolbar={
          !showingAll ? (
            <Button
              variant="outlined"
              color="neutral"
              disabled={loadingAll}
              onClick={handleShowAllTrips}
              startIcon={loadingAll ? <CircularProgress size={16} color="inherit" /> : <HistoryRoundedIcon />}
            >
              {loadingAll ? t('loading', { defaultValue: 'Loading…' }) : showAllLabel}
            </Button>
          ) : null
        }
        onRowClick={(row) => {
          if (row.id) navigate(`/bookings/bookingdetails/${row.id}`);
        }}
        rowLabel={(row) => row.reference || ''}
        prefsKey="user_trips"
        stateKey={id ? `user_trips_${id}` : undefined}
        exportCsv={isAdmin && allowPrivate}
        exportPrint={isAdmin}
        exportColumns={exportColumns}
        exportFileName="trips"
        exportTitle={title}
        exportSubtitle={`${data?.firstName || ''} ${data?.lastName || ''}`.trim()}
        minWidth={1180}
        maxHeight={null}
        emptyState={({ query }) =>
          query
            ? null
            : showingAll
              ? { icon: <RouteOutlinedIcon />, title: t('user_trips_empty', { defaultValue: 'No trips yet' }) }
              : {
                  icon: <RouteOutlinedIcon />,
                  title: t('user_trips_empty_recent', { defaultValue: 'No trips in the last 30 days' }),
                  actionLabel: showAllLabel,
                  onAction: handleShowAllTrips,
                }
        }
      />
    </Box>
  );
}
